import { getMultiGamesCond } from '@opm/services';
import { useFilterCondData } from '@opm/components/FilterCommon/hooks/useFilterCondData';
import { useEffect, useMemo, useState } from 'react';
import {
  getZoneTypeFromPath,
  ZONE_CONFIG,
} from '../../ZoneTopCreatives/constants';

export interface IZoneGameItem {
  game_id: string;
  game_name: string;
  genre: string;
  genre_zh: string;
  cover?: string;
  entity_type?: string;
  id_type?: string;
}

export type TGameMap = Map<string, { name: string; cover: string }>;

/**
 * 两步 cond 加载：
 * Step 1: useFilterCondData → materials_config_games（带 sessionStorage 缓存）→ 按 genre 过滤
 * Step 2: multi_games_cond → 多游戏取并集的 channel 等筛选器数据
 */
export function useMultiGamesCond({ pathname }: { pathname: string }) {
  const [multiGamesCondLoading, setMultiGamesCondLoading] = useState(false);
  const [condData, setCondData] = useState<any>({});
  const [zoneGames, setZoneGames] = useState<IZoneGameItem[]>([]);

  const zoneType = useMemo(() => getZoneTypeFromPath(pathname), [pathname]);
  const zoneConfig = zoneType ? ZONE_CONFIG[zoneType] : null;

  // Step 1: 使用 useFilterCondData hook 获取 marketing cond（带缓存）
  const { loading: marketingCondLoading, condData: marketingCondData } =
    useFilterCondData({
      keys: ['ADGame'], // ADGame 的 requestKey 是 'materials_config_games'
      extrRequestKeys: [],
    });

  // 当 marketing cond 加载完后，按 genre 过滤游戏
  useEffect(() => {
    if (marketingCondLoading || !zoneConfig) return;
    const allGames: IZoneGameItem[] =
      marketingCondData?.materials_config_games || [];
    const filtered = allGames.filter(g => g.genre === zoneConfig.genre);
    setZoneGames(filtered);
  }, [marketingCondLoading, marketingCondData, zoneConfig?.genre]);

  // Step 2: zoneGames 就绪后，请求 multi_games_cond
  useEffect(() => {
    if (marketingCondLoading || zoneGames.length === 0) return;

    let cancelled = false;
    setMultiGamesCondLoading(true);

    const gamesParam = zoneGames.map(g => ({
      edition_unified_id: g.game_id,
      entity_type: g.entity_type || 'mobile',
      id_type: 'unified',
    }));

    getMultiGamesCond({
      games: gamesParam,
      request_type: [
        'region_language',
        'channel',
        'topic',
        'key_topic',
        'keyword_mutual_account_config',
        'region_country',
        'region_country',
      ],
      un_request_type: ['topic_trans'],
    })
      .then((result: any) => {
        if (!cancelled) setCondData(result || {});
      })
      .catch((err: any) => {
        console.error('multi_games_cond error:', err);
      })
      .finally(() => {
        if (!cancelled) setMultiGamesCondLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [marketingCondLoading, zoneGames]);

  const loading = marketingCondLoading || multiGamesCondLoading;

  // gameMap: game_id → { name, cover }
  const gameMap: TGameMap = useMemo(() => {
    const map = new Map<string, { name: string; cover: string }>();
    zoneGames.forEach(g => {
      map.set(g.game_id, { name: g.game_name, cover: g.cover || '' });
    });
    return map;
  }, [zoneGames]);

  // games 对象数组（用于 multi_games_max_metric）
  const games = useMemo(
    () =>
      zoneGames.map(g => ({
        edition_unified_id: g.game_id,
        entity_type: g.entity_type || 'mobile',
        id_type: 'unified' as const,
      })),
    [zoneGames],
  );

  return { loading, condData, zoneGames, gameMap, games, zoneConfig };
}
